import { useState, useMemo, useCallback } from "react";
import { compareValues } from "../utils/spreadsheetHelpers";

/**
 * Generic, PRESENTATION-ONLY 3-state sort (asc → desc → none) for the OS stage tables.
 *
 * This is a sibling of `useSpreadsheetSort.ts`, not a replacement — that hook's 2-state
 * toggle (asc↔desc, no reset) is depended on by four other spreadsheets and is left
 * untouched. The OS tables need a third "back to natural order" state instead, hence a
 * separate hook.
 *
 * `sortedItems` always carries the ORIGINAL index into `items` alongside each item, so
 * callers can keep addressing rows by their real array position (patch/reg/selected all
 * stay correct) even while the table is rendered in sorted order.
 */
export type ViewSortDirection = "asc" | "desc";

export const useTableViewSort = <T,>(
  items: T[],
  getValue: (item: T, field: string) => string | number
) => {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<ViewSortDirection>("asc");

  const handleSort = useCallback(
    (field: string) => {
      if (sortField !== field) {
        setSortField(field);
        setSortDirection("asc");
        return;
      }
      if (sortDirection === "asc") {
        setSortDirection("desc");
        return;
      }
      // desc → reset to natural order
      setSortField(null);
      setSortDirection("asc");
    },
    [sortField, sortDirection]
  );

  const resetSort = useCallback(() => {
    setSortField(null);
    setSortDirection("asc");
  }, []);

  const isSorted = sortField !== null;

  const sortedItems = useMemo(() => {
    const withIndex = items.map((item, index) => ({ item, index }));
    if (!sortField) return withIndex;
    return withIndex
      .map(({ item, index }) => ({ item, index, value: getValue(item, sortField) }))
      .sort((a, b) => compareValues(a.value, b.value, sortDirection))
      .map(({ item, index }) => ({ item, index }));
  }, [items, sortField, sortDirection, getValue]);

  return {
    sortField,
    sortDirection,
    handleSort,
    resetSort,
    isSorted,
    sortedItems,
  };
};
